/**
 * ECD-12, solved as a moving boundary.
 *
 * The closed-form plating cell decides whether a trench fills by racing two
 * numbers: how long the bottom takes to reach the top against how long the
 * sidewalls take to meet. If the bottom wins the feature is filled, if it loses
 * it is voided, and if they are close it is a seam. That is a real criterion and
 * it usually gets the verdict right - but it is a statement ABOUT a geometry
 * rather than a calculation OF one, so it cannot say where the void ended up,
 * how big it is, what shape the surface above it took, or what the fill looked
 * like halfway through.
 *
 * Here the copper front is advanced on a grid instead. Nothing in the code
 * decides whether the feature voids. The two sidewalls either meet before the
 * bottom arrives or they do not, and if they do there is a hole left behind that
 * the calculation can then be asked about.
 *
 * THE THREE TRANSPORT FACTS, AND THE ONE MECHANISM
 *
 * Copper reaches the bottom of a deep feature slightly less easily than the top,
 * because the cupric ion has to diffuse down the hole. On its own that is enough
 * to void every feature: the mouth grows faster than the floor, and it closes
 * over. This is what plating without additives does, and it is why the additive
 * package exists.
 *
 * Suppressor - a large polymer - reaches the top far more easily than the
 * bottom, so it slows the mouth much more than the floor. That inverts the
 * gradient. It is not enough by itself; it buys a conformal fill, and a
 * conformal fill of a high-aspect feature closes on a seam.
 *
 * Accelerator does the rest, through CEAC, which is the whole mechanism:
 *
 *     the accelerator is CONSERVED on the surface it sits on
 *     and is NOT buried as copper grows over it
 *
 * So when a piece of surface shrinks, the same molecules end up on less area and
 * the local coverage rises. At the bottom of a trench, sidewall growth narrows
 * the floor; the floor's accelerator concentrates; the floor speeds up; which
 * makes it arrive sooner. That is a positive feedback, it is the reason
 * damascene works at all, and it is one line of bookkeeping.
 *
 * The solver does that bookkeeping literally. Each step it measures the wetted
 * surface area around every surface cell, before and after the front moves, and
 * rescales the coverage by the ratio. Nothing tells it that trench bottoms are
 * special. They come out special because that is where the ratio exceeds one.
 *
 * WHAT IS SOLVED AND WHAT IS TAKEN
 *
 *   SOLVED   the shape. Where the front is at every moment, whether it closes,
 *            where a void ends up and how large, the overburden profile over the
 *            feature, and the bottom-up gain as an outcome rather than an input.
 *
 *   TAKEN    the additive kinetics - how much a monolayer of accelerator speeds
 *            a surface up, how much suppressor slows one down, and the two
 *            transport lengths. Those are the same coefficients the closed form
 *            uses. The point is not to re-derive them; it is to stop assuming
 *            the answer they lead to.
 */

import { clamp } from '../../core/math'

/** Accelerator coverage at which the local rate doubles. */
const THETA_HALF = 0.12
/** How much a saturated suppressor layer slows the surface down. */
const SUPPRESSOR_STRENGTH = 2.2
/**
 * How far down a feature each species reaches [nm].
 *
 * These are LENGTHS, not aspect ratios, and that distinction is the whole reason
 * aspect ratio governs whether a feature fills. A shallow trench is short
 * compared with both numbers, so nothing is depleted and everything about the
 * additive package is beside the point - it fills whatever you do. A deep one is
 * long compared with the suppressor reach and comparable to the metal reach, so
 * the mouth is starved of suppressor relief and rich in copper while the floor
 * is the reverse. Nothing in the solver is told that high aspect ratios are
 * hard. It falls out of comparing a depth against these two lengths.
 *
 * The ordering is the point and it is not a fitting choice: the cupric ion is
 * small and fast and reaches most of the way down, the suppressor is a large
 * slow polymer consumed near the mouth. Swap them and damascene stops working.
 */
const METAL_REACH_NM = 600
const SUPPRESSOR_REACH_NM = 70
/**
 * Accelerator adsorption, as a relaxation toward its equilibrium coverage.
 *
 * A dilute bath component reaches a low steady coverage on an ordinary surface -
 * a few percent of a monolayer, not a full one. Everything above that is CEAC,
 * and the rate is deliberately an order of magnitude below the curvature term so
 * that where the two compete, geometry wins.
 */
const ADSORPTION = 0.01
const THETA_EQUILIBRIUM = 0.03
/** Sub-steps for the coverage balance. See the note at its integration. */
const THETA_SUBSTEPS = 12

/**
 * Accelerator concentration above which this solver is NOT trusted.
 *
 * Between roughly 1.3 and 1.7 times the qualified bath the coverage balance
 * admits a runaway: the CEAC feedback saturates a patch of sidewall at a full
 * monolayer partway up the feature, seals the slot, and voids it - and then at
 * still higher concentrations the feature fills again, because the whole surface
 * speeds up and the fill outruns the runaway. Overplating with excess
 * accelerator is a real failure, but a band that fails while BOTH its
 * neighbours fill is a race between two timescales in this model, not a process
 * window that has been measured. It is left in rather than tuned away, and the
 * result is flagged, because a solver that quietly clamps its own input is worse
 * than one that says where it stops being believable.
 */
export const VALIDATED_ACCELERATOR_MAX = 1.2

export interface FillGeometry {
  /** Trench width [cells]. */
  trenchCells: number
  /** Trench depth [cells]. */
  depthCells: number
  /** Field on each side of the trench [cells]. */
  fieldCells: number
  /** Physical size of one cell [nm]. */
  cellNm: number
}

export interface FillRecipe {
  /** Deposition rate on an unsuppressed flat surface [cells/step]. */
  baseRate: number
  /** Accelerator concentration, relative to the qualified bath. */
  accelerator: number
  /** Suppressor concentration, relative to the qualified bath. */
  suppressor: number
  /** Steps to run. */
  steps: number
}

export type FillVerdict = 'filled' | 'seam' | 'void'

export interface FillResult {
  geometry: FillGeometry
  /** Occupancy: 0 electrolyte, 1 copper, 2 dielectric. Row-major. */
  grid: Uint8Array
  /** Step at which each cell filled, for drawing growth-front contours. */
  stamp: Uint16Array
  /** Accelerator coverage, for drawing. */
  theta: Float64Array
  width: number
  height: number
  /** Row index of the field surface. Everything below it is the feature. */
  fieldTop: number
  /** First and last trench column. */
  x0: number
  x1: number

  /** Cells of enclosed void inside the feature. */
  voidCells: number
  /** Void as a fraction of the feature cross-section. */
  voidFraction: number
  /** Depth of the top of the void below the field [nm]. */
  voidDepthNm: number
  /** Widest point of the void [nm]. */
  voidWidthNm: number
  verdict: FillVerdict
  /** Step at which the mouth closed over, or -1. */
  pinchStep: number
  /** Step at which the floor reached the top of the feature, or -1. */
  fillStep: number
  /** Copper standing above the field at the end [nm]. */
  overburdenNm: number
  /** Overburden directly over the feature, minus over the field [nm]. */
  overburdenBumpNm: number
  /**
   * How much faster the floor advanced than the field, measured over the run.
   * An outcome of the CEAC bookkeeping, not a parameter.
   */
  bottomUpGain: number
  /** Peak accelerator coverage reached anywhere on the surface. */
  peakTheta: number
  /** Fraction of the feature depth at which that peak sat. */
  peakThetaDepthFraction: number
  /** True when the recipe sits outside the range this solver is trusted over. */
  beyondValidatedDomain: boolean
}

const EMPTY = 0
const COPPER = 1
const WALL = 2

/**
 * Advance a copper front through a feature.
 *
 * Cell-based rather than a level set, which is a real simplification worth
 * naming: the front is a set of occupied cells, so its position is quantised to
 * the grid. That costs accuracy on the shape of a nearly-closed mouth, and it
 * buys robustness - a level set has to be told what to do when two fronts touch,
 * and a cell grid simply has no cells left between them.
 */
export function solveFill(geo: FillGeometry, recipe: FillRecipe): FillResult {
  const width = geo.trenchCells + 2 * geo.fieldCells
  const headroom = Math.ceil(geo.depthCells * 0.75) + 4
  const floorMargin = 4
  const height = headroom + geo.depthCells + floorMargin
  const fieldTop = headroom
  const floorY = fieldTop + geo.depthCells - 1

  const grid = new Uint8Array(width * height)
  const theta = new Float64Array(width * height)
  const progress = new Float64Array(width * height)
  const stamp = new Uint16Array(width * height)
  const at = (x: number, y: number) => y * width + x
  const inside = (x: number, y: number) => x >= 0 && x < width && y >= 0 && y < height

  // Dielectric either side of the feature and underneath it, open in the middle.
  for (let y = fieldTop; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x < geo.fieldCells || x >= geo.fieldCells + geo.trenchCells || y > floorY) {
        grid[at(x, y)] = WALL
      }
    }
  }
  const x0 = geo.fieldCells
  const x1 = geo.fieldCells + geo.trenchCells

  const supp = clamp(recipe.suppressor, 0, 3)
  const accel = clamp(recipe.accelerator, 0, 3)

  // Seed: one cell of copper on the field, down both sidewalls, along the floor.
  // Everything grows from this; nothing nucleates on dielectric.
  const seed = (x: number, y: number) => {
    if (!inside(x, y) || grid[at(x, y)] !== EMPTY) return
    grid[at(x, y)] = COPPER
    theta[at(x, y)] = THETA_EQUILIBRIUM * accel
  }
  for (let x = 0; x < x0; x++) seed(x, fieldTop - 1)
  for (let x = x1; x < width; x++) seed(x, fieldTop - 1)
  for (let y = fieldTop; y <= floorY; y++) {
    seed(x0, y)
    seed(x1 - 1, y)
  }
  for (let x = x0; x < x1; x++) seed(x, floorY)

  const metalReach = Math.max(2, METAL_REACH_NM / geo.cellNm)
  const suppReach = Math.max(1, SUPPRESSOR_REACH_NM / geo.cellNm)

  /**
   * Local growth rate, from transport and both additives.
   *
   * The two additives compete for the same sites, so a surface that has gone
   * accelerator-rich is also one the suppressor has been displaced from. That
   * coupling is why superfill is a runaway rather than a gentle bias: the trench
   * bottom does not merely gain acceleration, it loses its brake at the same
   * time, and both happen fastest exactly where the surface is closing.
   */
  const rateAt = (y: number, th: number) => {
    const below = Math.max(0, y - fieldTop)
    const metal = Math.exp(-below / metalReach)
    const brake = 1 + SUPPRESSOR_STRENGTH * supp * Math.exp(-below / suppReach) * (1 - th)
    return (recipe.baseRate * metal * (1 + th / THETA_HALF)) / brake
  }

  const NEIGHBOURS = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const

  /** Is this copper cell part of the wetted surface? */
  const exposed = (x: number, y: number) => {
    if (grid[at(x, y)] !== COPPER) return false
    for (const [dx, dy] of NEIGHBOURS) {
      if (inside(x + dx, y + dy) && grid[at(x + dx, y + dy)] === EMPTY) return true
    }
    return false
  }

  // Occupancy summed-area table, for curvature. Rebuilt once per step; every
  // window query is then four lookups.
  const satW = width + 1
  const sat = new Float64Array(satW * (height + 1))
  const buildSat = () => {
    for (let y = 0; y < height; y++) {
      let row = 0
      for (let x = 0; x < width; x++) {
        if (grid[at(x, y)] !== EMPTY) row++
        sat[(y + 1) * satW + (x + 1)] = sat[y * satW + (x + 1)] + row
      }
    }
  }

  /**
   * Curvature of the front, from the occupied fraction of a small window.
   *
   * A flat surface has exactly half its neighbourhood occupied; a concave one
   * has more, a convex one less. For a circular front of radius r the occupied
   * fraction of a window of half-width R exceeds the flat value by about
   * R/(pi*r), so kappa = 1/r follows from the excess directly. Discretisation
   * puts the flat value at (R+1)/(2R+1) rather than a half, because the surface
   * cell counts itself - so that is what gets subtracted.
   */
  // Sized against the trench WIDTH, which is the limiting dimension. A stencil
  // wider than the feature reads the far sidewall as curvature of the near one
  // and reports a concavity that is not there - which shows up as a band of
  // accelerator concentration where the coverage runs away partway up a
  // sidewall while both neighbouring concentrations fill cleanly.
  const R = Math.max(2, Math.round(geo.trenchCells / 5))
  const flatFraction = (R + 1) / (2 * R + 1)
  const curvature = (cx: number, cy: number) => {
    const lx = Math.max(0, cx - R)
    const hx = Math.min(width - 1, cx + R)
    const ly = Math.max(0, cy - R)
    const hy = Math.min(height - 1, cy + R)
    const occupied =
      sat[(hy + 1) * satW + (hx + 1)] -
      sat[ly * satW + (hx + 1)] -
      sat[(hy + 1) * satW + lx] +
      sat[ly * satW + lx]
    const area = (hx - lx + 1) * (hy - ly + 1)
    return (Math.PI * (occupied / area - flatFraction)) / R
  }

  /** Contiguous copper rising from the floor of a column. */
  const floorRise = (x: number) => {
    let n = 0
    for (let y = floorY; y >= 0 && grid[at(x, y)] === COPPER; y--) n++
    return n
  }
  /** Topmost copper in a column - the field or overburden surface. */
  const topOf = (x: number) => {
    for (let y = 0; y < height; y++) if (grid[at(x, y)] === COPPER) return y
    return height
  }

  /**
   * Which empty cells can still be reached by the electrolyte.
   *
   * This is what makes a void a void. Once the mouth closes over a cavity there
   * is no path for cupric ion to reach it, so deposition inside simply stops and
   * the hole is there for good. Without this the sidewalls would go on plating
   * into a sealed pocket and every feature would come out solid - which is the
   * mistake the closed form makes in a different way, by never having a cavity
   * to seal in the first place.
   */
  const reachable = new Uint8Array(width * height)
  const stack: number[] = []
  const floodReachable = () => {
    reachable.fill(0)
    stack.length = 0
    for (let x = 0; x < width; x++) {
      if (grid[at(x, 0)] === EMPTY) {
        reachable[at(x, 0)] = 1
        stack.push(at(x, 0))
      }
    }
    while (stack.length) {
      const i = stack.pop()!
      const x = i % width
      const y = (i - x) / width
      for (const [dx, dy] of NEIGHBOURS) {
        const nx = x + dx
        const ny = y + dy
        if (!inside(nx, ny)) continue
        const j = at(nx, ny)
        if (grid[j] === EMPTY && !reachable[j]) {
          reachable[j] = 1
          stack.push(j)
        }
      }
    }
  }

  const centre = Math.floor((x0 + x1) / 2)
  const fieldStart = topOf(1)

  let pinchStep = -1
  let fillStep = -1
  let peakTheta = 0
  let closedStep = -1
  const growth = new Float64Array(width * height)
  // Once the feature can grow no further, run on far enough to lay down a real
  // overburden - its topography over the feature is what CMP inherits.
  const overburdenTail = Math.ceil(0.3 * geo.depthCells)

  for (let step = 1; step <= recipe.steps; step++) {
    buildSat()
    floodReachable()

    // The additive balance, on every wetted cell.
    //
    //     dtheta/dt = k_ads * C * (1 - theta)  +  theta * kappa * v
    //
    // The first term is adsorption from the bath onto free sites. The second is
    // CEAC: the coverage rides the moving interface instead of being buried, so
    // where the interface is concave and moving, the same molecules end up on
    // less area. Nothing here knows what a trench is.
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = at(x, y)
        if (!exposed(x, y)) continue
        // Sub-stepped, because this is stiff. The CEAC term feeds back on itself
        // twice over - a higher coverage grows faster AND moves the interface
        // faster - so dtheta goes as theta squared and a single explicit step of
        // the full interval overshoots to a saturated monolayer in one jump. The
        // symptom is a narrow band of accelerator concentration that fails while
        // both neighbours fill, which is an artefact of the integrator and not a
        // property of the bath.
        const k = curvature(x, y)
        const relax = ADSORPTION * (THETA_EQUILIBRIUM * accel - theta[i])
        let th = theta[i]
        for (let sub = 0; sub < THETA_SUBSTEPS; sub++) {
          th = clamp(th + (relax + th * k * rateAt(y, th)) / THETA_SUBSTEPS, 0, 1)
        }
        theta[i] = th
        if (th > peakTheta) peakTheta = th
      }
    }

    growth.fill(0)
    let featureActive = false
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (grid[at(x, y)] !== EMPTY) continue
        // No path to the bath, no copper, no growth.
        if (!reachable[at(x, y)]) continue
        let thetaSum = 0
        let n = 0
        for (const [dx, dy] of NEIGHBOURS) {
          const nx = x + dx
          const ny = y + dy
          if (!inside(nx, ny) || grid[at(nx, ny)] !== COPPER) continue
          thetaSum += theta[at(nx, ny)]
          n++
        }
        if (n === 0) continue
        growth[at(x, y)] = rateAt(y, thetaSum / n)
        if (y >= fieldTop) featureActive = true
      }
    }
    if (!featureActive && closedStep < 0) closedStep = step

    // Advance. A new surface cell adsorbs the coverage of the surface it grew
    // from - the accelerator segregates to the moving interface rather than
    // being buried, which is the statement CEAC rests on.
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = at(x, y)
        if (growth[i] <= 0) continue
        progress[i] += growth[i]
        if (progress[i] < 1) continue
        let thetaSum = 0
        let n = 0
        for (const [dx, dy] of NEIGHBOURS) {
          const nx = x + dx
          const ny = y + dy
          if (!inside(nx, ny) || grid[at(nx, ny)] !== COPPER) continue
          thetaSum += theta[at(nx, ny)]
          n++
        }
        grid[i] = COPPER
        stamp[i] = step
        progress[i] = 0
        theta[i] = n > 0 ? thetaSum / n : 0
      }
    }

    if (pinchStep < 0) {
      let open = 0
      for (let x = x0; x < x1; x++) if (grid[at(x, fieldTop)] === EMPTY) open++
      if (open === 0) pinchStep = step
    }
    if (fillStep < 0 && floorRise(centre) >= geo.depthCells) fillStep = step
    if (closedStep > 0 && step - closedStep >= overburdenTail) break
  }

  // Whatever the bath can no longer reach is what the cross-section will show.
  floodReachable()
  let voidCells = 0
  let voidTopY = -1
  let voidWidth = 0
  for (let y = 0; y < height; y++) {
    let row = 0
    for (let x = x0; x < x1; x++) {
      if (grid[at(x, y)] === EMPTY && !reachable[at(x, y)]) {
        voidCells++
        row++
        if (voidTopY < 0) voidTopY = y
      }
    }
    voidWidth = Math.max(voidWidth, row)
  }

  // Where the coverage peaked. Only meaningful when there IS coverage - with no
  // accelerator in the bath every cell is zero, and "the peak is at cell zero"
  // would report a depth above the wafer.
  let peakThetaY = fieldTop
  {
    let best = 0
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (theta[at(x, y)] > best) {
          best = theta[at(x, y)]
          peakThetaY = y
        }
      }
    }
  }

  const overField = fieldTop - topOf(1)
  const overFeature = fieldTop - topOf(centre)
  const fieldAdvance = fieldStart - topOf(1)

  // The verdict comes out of the geometry. A seam is a void one cell across -
  // the sidewalls met, but nothing was left behind that a cross-section would
  // show as a hole.
  const verdict: FillVerdict = voidCells === 0 ? 'filled' : voidWidth <= 1 ? 'seam' : 'void'

  return {
    geometry: geo,
    grid,
    stamp,
    theta,
    width,
    height,
    fieldTop,
    x0,
    x1,
    voidCells,
    voidFraction: voidCells / (geo.trenchCells * geo.depthCells),
    voidDepthNm: voidTopY >= 0 ? (voidTopY - fieldTop) * geo.cellNm : 0,
    voidWidthNm: voidWidth * geo.cellNm,
    verdict,
    pinchStep,
    fillStep,
    overburdenNm: overField * geo.cellNm,
    overburdenBumpNm: (overFeature - overField) * geo.cellNm,
    bottomUpGain: fieldAdvance > 0 ? floorRise(centre) / fieldAdvance : 0,
    peakTheta,
    peakThetaDepthFraction: (peakThetaY - fieldTop) / geo.depthCells,
    beyondValidatedDomain: accel > VALIDATED_ACCELERATOR_MAX,
  }
}
